import { Expose } from 'class-transformer';

import type ImageInterface from '../types/Image';
import type OptionGroupInterface from '../types/OptionGroup';
import type MetaDataInterface from '../types/MetaData';
import type ItemCustomData from '../types/CustomData';

export type OptionNutritionBuckets = {
  base: Map<number, string[]>;
  onTheSide: Map<number, string[]>;
};

export default class Product {
  @Expose() id: string = "";
  @Expose() chainproductid: string = "";
  @Expose() name?: string = "";
  @Expose() description?: string = "";
  @Expose() images?: ImageInterface[] = [];
  @Expose() optiongroups?: OptionGroupInterface[] = [];
  @Expose() options?: number[] = [];
  @Expose() cost: number = 0;
  @Expose() metadata?: MetaDataInterface[] = [];
  @Expose() quantity: number = 1;
  @Expose() feeds: number[] = [0,0,0];
  @Expose() custom_data: ItemCustomData = {
    addon_products: {}
  }
  @Expose() cost_with_defaults: number = 0;
  @Expose() allergens: string[] = [];
  @Expose() diets: string[] = [];
  @Expose() allergens_with_defaults: string[] = [];
  @Expose() diets_with_defaults: string[] = [];
  @Expose() quick_combo_item: boolean = false;

  constructor(id: string, name: string, chainproductid: string, optiongroups: OptionGroupInterface[], options: number[], description: string,  cost: number, images: ImageInterface[], metadata: MetaDataInterface[], quantity: number, productaddons: Map<string, number>, feeds: number[], custom_data: ItemCustomData, cost_with_defaults: number, allergens: string[], diets: string[], allergens_with_defaults: string[], diets_with_defaults: string[], quick_combo_item: boolean) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.images = images;
    this.optiongroups = optiongroups;
    this.options = options;
    this.cost = cost;
    this.chainproductid = chainproductid;
    this.metadata = metadata;
    this.quantity = quantity;
    this.feeds = feeds;
    this.custom_data = custom_data;
    this.cost_with_defaults = cost_with_defaults;
    this.allergens = allergens;
    this.diets = diets;
    this.allergens_with_defaults = allergens_with_defaults;
    this.diets_with_defaults = diets_with_defaults;
    this.quick_combo_item = quick_combo_item;
  }

  getImageByKey = (key: string) => {
    if (this.images) {
      const keyedImages = this.images.filter(img => {
        return img.position == key
      })
      return keyedImages.length > 0 ? keyedImages[0] : null;
    } else {
      return null
    }
  }

  getMetaDataByKey = (key: string) => {
    let data = null;
    if (this.metadata) {
      data = this.metadata.find(md => {
        return md.key == key
      })
    }

    return data ? data.value : null;
  }

  isCatering = (): boolean => {
    let catering = false;

    if (this.getMetaDataByKey('catering')) {
      catering = true;
    }

    return catering;
  }

  // feeds = ():number[] => {
  //   let arr = [0,0,0]

  //   if (this.getMetaDataByKey('feedCount')) {
  //     const meta = this.getMetaDataByKey('feedCount');
  //     arr = JSON.parse(meta)
  //   }

  //   return arr;
  // }

  priceWithDefaults = ():number => {
    let cost = this.cost;

    // cost_with_defaults is always >= cost, but throwing this check here to be safe
    if (this.cost_with_defaults && this.cost_with_defaults > 0) {
      cost = this.cost_with_defaults
    }

    return cost;
  }

  optionsNutrition = (type: string): OptionNutritionBuckets => {
    const nutrition: OptionNutritionBuckets = {
      base: new Map<number, string[]>(),
      onTheSide: new Map<number, string[]>(),
    };

    const mergeBuckets = (source: OptionNutritionBuckets) => {
      source.base.forEach((value, key) => {
        nutrition.base.set(key, value ?? []);
      });

      source.onTheSide.forEach((value, key) => {
        nutrition.onTheSide.set(key, value ?? []);
      });
    };

    if (this.optiongroups) {
      this.optiongroups.forEach((optiongroup) => {
        mergeBuckets(this.optionGroupOptionNutrition(optiongroup, type));
      });
    }

    return nutrition;
  }

  optionGroupOptionNutrition = (
    optiongroup: OptionGroupInterface,
    type: string,
    inheritsOnTheSide = false
  ): OptionNutritionBuckets => {
    const nutrition: OptionNutritionBuckets = {
      base: new Map<number, string[]>(),
      onTheSide: new Map<number, string[]>(),
    };

    const onTheSideMeta = optiongroup.metadata?.find((md) => md.key === 'onTheSide');
    const groupOnTheSide = inheritsOnTheSide || (
      onTheSideMeta != null &&
      onTheSideMeta.value !== null &&
      onTheSideMeta.value !== undefined &&
      onTheSideMeta.value !== false &&
      onTheSideMeta.value !== 'false'
    );

    const targetBucket = groupOnTheSide ? nutrition.onTheSide : nutrition.base;

    const assignOptionNutrition = (optionId: number, values: string[] | undefined) => {
      targetBucket.set(optionId, values ?? []);
    };

    if (optiongroup.options && optiongroup.options.length > 0) {
      optiongroup.options.forEach((option) => {
        switch (type) {
          case 'allergens':
            assignOptionNutrition(option.id, option.allergens);
            break;
          case 'dietary':
            assignOptionNutrition(option.id, option.diets);
            break;
          default:
            break;
        }

        if (option.optiongroups && option.optiongroups.length > 0) {
          option.optiongroups.forEach((nestedGroup) => {
            const nestedNutrition = this.optionGroupOptionNutrition(nestedGroup, type, groupOnTheSide);

            nestedNutrition.base.forEach((value, key) => {
              nutrition.base.set(key, value ?? []);
            });

            nestedNutrition.onTheSide.forEach((value, key) => {
              nutrition.onTheSide.set(key, value ?? []);
            });
          });
        }
      });
    }

    return nutrition;
  }

}
